import { Dialog, Modal, ModalOverlay } from "@admin-ui/components/primitives/application/modals/modal";
import { Tabs } from "@admin-ui/components/primitives/application/tabs/tabs";
import { Badge, BadgeWithDot } from "@admin-ui/components/primitives/base/badges/badges";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { CloseButton } from "@admin-ui/components/primitives/base/buttons/close-button";
import { FeaturedIcon } from "@admin-ui/components/primitives/foundations/featured-icon";
import type { AdminUsersFoundingMember, FoundingMemberStatus } from "@travily/types/models";
import { Star01 } from "@untitledui/icons";
import { Heading as AriaHeading } from "react-aria-components";

export interface FoundingMemberModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  userName: string | null;
  foundingMember: AdminUsersFoundingMember;
}

const statusColors: Record<FoundingMemberStatus, "gray" | "blue" | "warning" | "success"> = {
  not_started: "gray",
  in_progress: "blue",
  paused: "warning",
  completed: "success",
  skipped: "gray",
};

const statusLabels: Record<FoundingMemberStatus, string> = {
  not_started: "Not started",
  in_progress: "In progress",
  paused: "Paused",
  completed: "Completed",
  skipped: "Skipped",
};

const memberTypeLabels: Record<string, string> = {
  family: "Family",
  individual: "Individual",
  community: "Community",
};

type FieldSpec = { label: string; key: string; format?: "raw" };

const profileFields: Record<string, FieldSpec[]> = {
  family: [
    { label: "Worldschooling experience", key: "worldschooling_experience" },
    { label: "Hub experience", key: "hubs_experience" },
    { label: "Hubs visited", key: "hubs_visited", format: "raw" },
    { label: "Socials", key: "socials", format: "raw" },
    { label: "Newsletter consent", key: "newsletter_consent" },
  ],
  individual: [
    { label: "Years of experience", key: "educator_experience" },
    { label: "Hub experience", key: "hubs_experience" },
    { label: "Hubs worked at", key: "hubs_worked", format: "raw" },
    { label: "Socials", key: "socials", format: "raw" },
    { label: "Newsletter consent", key: "newsletter_consent" },
  ],
  community: [
    { label: "Hubs operated", key: "hubs_operated" },
    { label: "Hub formats", key: "hubs_formats" },
    { label: "Socials", key: "socials", format: "raw" },
    { label: "Newsletter consent", key: "newsletter_consent" },
  ],
};

function formatEnumValue(value: string): string {
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatValue(value: unknown, format?: "raw"): string {
  if (value == null) return "-";
  if (Array.isArray(value)) return value.length > 0 ? value.map(formatEnumValue).join(", ") : "-";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return format === "raw" ? value : formatEnumValue(value);
  return "-";
}

function FieldRow({ label, value, format }: { label: string; value: unknown; format?: "raw" }) {
  return (
    <div className="flex justify-between gap-4 py-2.5">
      <dt className="text-sm font-medium text-tertiary">{label}</dt>
      <dd className="text-right text-sm text-primary">{formatValue(value, format)}</dd>
    </div>
  );
}

function ProfileFieldList({ profile, fields }: { profile: Record<string, unknown>; fields: FieldSpec[] }) {
  return (
    <dl className="divide-y divide-secondary">
      {fields.map((field) => (
        <FieldRow key={field.key} label={field.label} value={profile[field.key]} format={field.format} />
      ))}
    </dl>
  );
}

type ProfileTab = { id: string; label: string };

function getAvailableTabs(fm: AdminUsersFoundingMember): ProfileTab[] {
  const tabs: ProfileTab[] = [];
  if (fm.family_profile) tabs.push({ id: "family", label: "Family" });
  if (fm.individual_profile) tabs.push({ id: "individual", label: "Individual" });
  if (fm.community_profile) tabs.push({ id: "community", label: "Community" });
  return tabs;
}

const profileForTab = (fm: AdminUsersFoundingMember, tabId: string): Record<string, unknown> | null => {
  if (tabId === "family") return fm.family_profile;
  if (tabId === "individual") return fm.individual_profile;
  if (tabId === "community") return fm.community_profile;
  return null;
};

export function FoundingMemberModal({ isOpen, onOpenChange, userName, foundingMember }: FoundingMemberModalProps) {
  const title = userName ? `${userName}'s founding member profile` : "Founding member profile";
  const tabs = getAvailableTabs(foundingMember);
  const defaultTab = tabs.find((t) => t.id === foundingMember.member_type)?.id ?? tabs[0]?.id;

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange} isDismissable>
      <Modal>
        <Dialog>
          {({ close }) => (
            <div className="pointer-events-auto relative w-full overflow-hidden rounded-2xl bg-primary shadow-xl sm:max-w-120">
              <CloseButton
                onPress={close}
                theme="light"
                size="sm"
                className="absolute top-3 right-3 z-10 sm:top-4 sm:right-4"
              />
              <div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
                <div className="relative w-max">
                  <FeaturedIcon color="gray" size="md" theme="modern" icon={Star01} />
                </div>
                <div className="z-10 flex flex-col gap-1.5">
                  <AriaHeading slot="title" className="text-md font-semibold text-primary">
                    {title}
                  </AriaHeading>
                  <div className="flex gap-2">
                    <BadgeWithDot color={statusColors[foundingMember.status]} size="sm">
                      {statusLabels[foundingMember.status]}
                    </BadgeWithDot>
                    {foundingMember.member_type && (
                      <Badge color="purple" size="sm">
                        {memberTypeLabels[foundingMember.member_type] ?? foundingMember.member_type}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {tabs.length > 0 && (
                <div className="px-4 pt-4 sm:px-6 sm:pt-5">
                  <Tabs defaultSelectedKey={defaultTab}>
                    <Tabs.List type="underline" size="sm">
                      {tabs.map((tab) => (
                        <Tabs.Item key={tab.id} id={tab.id}>
                          {tab.label}
                        </Tabs.Item>
                      ))}
                    </Tabs.List>

                    {tabs.map((tab) => {
                      const profile = profileForTab(foundingMember, tab.id);
                      const fields = profileFields[tab.id];
                      if (!profile || !fields) return null;
                      return (
                        <Tabs.Panel key={tab.id} id={tab.id} className="pt-1">
                          <ProfileFieldList profile={profile} fields={fields} />
                        </Tabs.Panel>
                      );
                    })}
                  </Tabs>
                </div>
              )}

              {tabs.length === 0 && (
                <div className="px-4 pt-4 sm:px-6 sm:pt-5">
                  <p className="text-sm text-tertiary">No profile data yet.</p>
                </div>
              )}

              <div className="p-4 pt-6 sm:px-6 sm:pt-8 sm:pb-6">
                <Button color="secondary" size="md" onClick={close} className="w-full">
                  Close
                </Button>
              </div>
            </div>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
